import React, { useState, useEffect } from 'react';
import client from '../api/client';
import { ShieldAlert, Clock, User, Activity, AlertTriangle, ArrowDown } from 'lucide-react';

const Logs = () => {
    const [logs, setLogs] = useState([]);
    const [offset, setOffset] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const LIMIT = 30;

    const fetchLogs = async (currentOffset) => {
        setIsLoading(true);
        try {
            const res = await client.get('/logs', { params: { limit: LIMIT, offset: currentOffset } });
            const newLogs = res.data;

            if (newLogs.length < LIMIT) {
                setHasMore(false);
            }

            setLogs(prev => currentOffset === 0 ? newLogs : [...prev, ...newLogs]);
        } catch (error) {
            console.error("Failed to fetch logs", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs(0);
    }, []);

    const handleLoadMore = () => {
        const newOffset = offset + LIMIT;
        setOffset(newOffset);
        fetchLogs(newOffset);
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-20">
            <div>
                <h1 className="text-3xl font-black text-white tracking-tighter uppercase text-rose-500">Системные Логи</h1>
                <p className="text-neutral-500 text-[10px] font-black uppercase tracking-[0.2em] mt-2">Аудит действий пользователей и изменений</p>
            </div>

            <div className="bg-neutral-900 border border-neutral-800 rounded-[40px] shadow-2xl overflow-hidden backdrop-blur-xl">
                <div className="divide-y divide-neutral-800/50">
                    {/* Desktop Header */}
                    <div className="hidden md:flex items-center px-8 py-5 bg-neutral-800/50 border-b border-neutral-800">
                        <div className="w-48 text-[10px] font-black text-neutral-500 uppercase tracking-widest">Время</div>
                        <div className="w-48 text-[10px] font-black text-neutral-500 uppercase tracking-widest">Пользователь</div>
                        <div className="w-56 text-[10px] font-black text-neutral-500 uppercase tracking-widest">Действие</div>
                        <div className="flex-1 text-[10px] font-black text-neutral-500 uppercase tracking-widest">Детали</div>
                    </div>

                    {logs.map((log) => (
                        <div key={log.id} className="group flex flex-col md:flex-row md:items-center px-8 py-6 md:py-5 hover:bg-neutral-800/30 transition-all gap-4 md:gap-0">
                            {/* Time */}
                            <div className="md:w-48 flex items-center justify-between md:block">
                                <span className="md:hidden text-[8px] font-black text-neutral-600 uppercase tracking-widest">Время</span>
                                <span className="text-xs font-mono text-neutral-500">
                                    {new Date(log.timestamp).toLocaleString('ru-RU')}
                                </span>
                            </div>

                            {/* User */}
                            <div className="md:w-48 flex items-center justify-between md:block">
                                <span className="md:hidden text-[8px] font-black text-neutral-600 uppercase tracking-widest">Пользователь</span>
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-neutral-800 border border-neutral-700 flex items-center justify-center text-[10px] font-black text-white shadow-inner">
                                        {log.user[0].toUpperCase()}
                                    </div>
                                    <span className="text-xs font-black text-neutral-300 uppercase tracking-tight italic">{log.user}</span>
                                </div>
                            </div>

                            {/* Action */}
                            <div className="md:w-56 flex items-center justify-between md:block">
                                <span className="md:hidden text-[8px] font-black text-neutral-600 uppercase tracking-widest">Действие</span>
                                <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border ${
                                    log.action.includes('Delete') || log.action.includes('Archive') ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' :
                                    log.action.includes('Update') || log.action.includes('EDIT') ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 
                                    'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                                }`}>
                                    {(() => {
                                        const actionMap = {
                                            'create_transaction': 'Создание транзакции',
                                            'Create Transaction': 'Создание транзакции',
                                            'Update Transaction': 'Обновление транзакции',
                                            'Delete Transaction': 'Удаление транзакции',
                                            'DELETE': 'Удаление транзакции',
                                            'EDIT': 'Изменение транзакции',
                                            'TRANSFER': 'Перевод средств',
                                            'Export Transactions': 'Экспорт транзакций',
                                            'Create Project': 'Создание проекта',
                                            'Update Project': 'Обновление проекта',
                                            'Delete Project': 'Удаление проекта',
                                            'Create Inventory': 'Создание ТМЦ',
                                            'Create Inventory Item': 'Создание ТМЦ',
                                            'Update Inventory': 'Обновление ТМЦ',
                                            'Update Inventory Item': 'Обновление ТМЦ',
                                            'Delete Inventory': 'Удаление ТМЦ',
                                            'Delete Inventory Item': 'Удаление ТМЦ',
                                            'Create Planned Item': 'Создание планирования',
                                            'Update Planned Item': 'Обновление планирования',
                                            'Delete Planned Item': 'Удаление планирования',
                                            'Create Category': 'Создание категории',
                                            'Delete Category': 'Удаление категории',
                                            'Create User': 'Создание пользователя',
                                            'Update User': 'Обновление пользователя',
                                            'Delete User': 'Удаление пользователя',
                                            'Create Wallet': 'Создание кошелька',
                                            'Update Wallet': 'Обновление кошелька',
                                            'Delete Wallet': 'Удаление кошелька',
                                            'Login': 'Вход в систему',
                                            'Logout': 'Выход из системы',
                                            'BOT': 'Действие бота'
                                        };
                                        return actionMap[log.action] || log.action;
                                    })()}
                                </span>
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                                <span className="md:hidden text-[8px] font-black text-neutral-600 uppercase tracking-widest block mb-1">Детали</span>
                                <p className="text-xs text-neutral-400 font-medium leading-relaxed md:truncate group-hover:whitespace-normal transition-all">
                                    {log.details}
                                </p>
                            </div>
                        </div>
                    ))}

                    {logs.length === 0 && !isLoading && (
                        <div className="py-32 text-center">
                            <Activity className="w-12 h-12 text-neutral-800 mx-auto mb-4 opacity-20" />
                            <p className="text-[10px] font-black text-neutral-600 uppercase tracking-[0.3em]">
                                Логи пусты
                            </p>
                        </div>
                    )}
                </div>
                {hasMore && (
                    <div className="p-4 bg-neutral-800/20 border-t border-neutral-800 flex justify-center">
                        <button
                            onClick={handleLoadMore}
                            disabled={isLoading}
                            className="flex items-center gap-2 px-6 py-3 bg-neutral-800 hover:bg-neutral-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50"
                        >
                            {isLoading ? 'Загрузка...' : (
                                <>
                                    <ArrowDown className="w-4 h-4" />
                                    Загрузить еще
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <div className="p-8 bg-black/20 border border-neutral-800 rounded-3xl flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-rose-500 shrink-0" />
                <div>
                    <p className="text-sm font-bold text-white mb-1 uppercase tracking-tight">Внимание</p>
                    <p className="text-xs text-neutral-500 leading-relaxed">Данные логи предназначены только для внутреннего аудита. Удаление логов невозможно для обеспечения безопасности и прозрачности финансовых операций.</p>
                </div>
            </div>
        </div>
    );
};

export default Logs;
